import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";

import { Mark } from "../components/Mark";
import { buttonClasses } from "../components/primitives";
import { api } from "../lib/api";

/**
 * Where a recognised but not-yet-approved account lands.
 *
 * Distinct from the login page on purpose: bouncing someone back to a sign-in
 * they have already completed reads as a broken login, and they would try it
 * again.
 *
 * **It holds a session, and that is what lets it keep its promise.** A pending
 * account used to get none, so this page polled a session that could never
 * turn active and said it would update itself when it could not. On the beta
 * every approved person came back through the link they already had, found it
 * spent, and read that as the login being broken. The session reaches nothing
 * — the server refuses every gated route with `pending` — but it can be asked
 * whether the answer has changed, and the admin's approval now mails them too.
 */
export function Pending() {
  const session = useQuery({
    queryKey: ["session"],
    queryFn: api.session,
    refetchInterval: 15_000,
  });
  // Public, so this works before there is a session to speak of.
  const playbooks = useQuery({ queryKey: ["playbooks"], queryFn: api.playbooks });

  // Their choice of playbook, carried this far so it is not silently lost.
  // Registration is closed, so for the audience the landing page is *for* the
  // journey ends here rather than at a prefilled form — saying the choice was
  // kept is what makes the tile's promise honest instead of untrue.
  const [params] = useSearchParams();
  const playbook = params.get("playbook");
  const chosen = playbooks.data?.find((p) => p.key === playbook);
  const login = `/login${playbook ? `?playbook=${encodeURIComponent(playbook)}` : ""}`;

  if (session.data?.authenticated) {
    window.location.replace(playbook ? `/new/${encodeURIComponent(playbook)}` : "/");
    return null;
  }

  const status = session.data?.status ?? null;
  const email = session.data?.email;

  return (
    <div className="flex min-h-dvh items-center justify-center px-5 py-16">
      <div className="w-full max-w-sm text-center">
        <Mark size={60} animate className="mx-auto" />
        <h1 className="mt-7 text-lg font-medium tracking-tight text-ink">
          {status === "suspended"
            ? "This account is suspended"
            : status === "pending"
              ? "Waiting for approval"
              : session.isLoading
                ? "Checking your account…"
                : "You are signed out"}
        </h1>

        {status === "pending" && (
          <div className="mt-3 space-y-3 text-sm leading-relaxed text-ink-2">
            <p>
              Your account <span className="font-medium text-ink">{email}</span>{" "}
              exists, and the administrators have been told it is waiting.
            </p>
            <p>
              We will email you when it is approved. If this page is still
              open, it lets you in by itself.
            </p>
          </div>
        )}

        {status === "suspended" && (
          <p className="mt-3 text-sm leading-relaxed text-ink-2">
            An administrator has withdrawn access for{" "}
            <span className="font-medium text-ink">{email}</span>. Speak to them
            if you think that is a mistake.
          </p>
        )}

        {status === null && !session.isLoading && (
          <>
            <p className="mt-3 text-sm leading-relaxed text-ink-2">
              Sign in again to see where your account stands. If it has been
              approved, you go straight in.
            </p>
            <Link to={login} className={`${buttonClasses("primary")} mt-7 w-full`}>
              Sign in
            </Link>
          </>
        )}

        {status === "pending" && chosen && (
          <p className="mt-5 rounded-lg border border-line bg-surface-2 px-4 py-3 text-xs leading-relaxed text-ink-2">
            We have kept your choice of{" "}
            <span className="font-medium text-ink">{chosen.title}</span>. You
            will land on it once your account is approved.
          </p>
        )}

        {status !== null && (
          <button
            onClick={() => api.logout().then(() => window.location.replace("/login"))}
            className={`${buttonClasses("ghost")} mt-8`}
          >
            {email ? `Not ${email}? Sign out` : "Sign out"}
          </button>
        )}
      </div>
    </div>
  );
}
